<template>
  <div class="container">
    <div class="col-md-8 col-sm-10 mx-auto">
      <div v-if="updateError">
        <b>Error submitting updates: </b>
        <p class="text-danger">{{ updateError }}</p>
      </div>
      <div v-if="editErrors.length">
        <b>Please correct the following error(s):</b>
        <ul>
          <li v-for="(error, index) in editErrors" v-bind:key="index" class="text-danger">{{ error }}</li>
        </ul>
      </div>
      <div class="form-group input-group">
        <div class="input-group-prepend">
          <span class="input-group-text"><font-awesome-icon icon="gamepad" fixed-width /></span>
        </div>
        <input
          type="text"
          class="form-control"
          placeholder="Game name"
          v-model="gameInfo.name"
        >
      </div>
      <div class="form-group input-group">
        <div class="input-group-prepend">
          <span class="input-group-text"><font-awesome-icon icon="key" fixed-width /></span>
        </div>
        <input
          type="text"
          class="form-control"
          placeholder="Game key"
          v-model="gameInfo.key"
        >
      </div>
      <div class="form-group input-group">
        <div class="input-group-prepend">
          <span class="input-group-text"><font-awesome-icon icon="edit" fixed-width /></span>
        </div>
        <input
          type="text"
          class="form-control"
          placeholder="Notes"
          v-model="gameInfo.notes"
        >
      </div>
      <div v-for="(row, index) in gameInfo.dlcs" v-bind:key="index">
        <hr/>
        <div class="form-group input-group">
          <div class="input-group-prepend">
            <span class="input-group-text"><font-awesome-icon icon="gamepad" fixed-width /></span>
          </div>
          <input
            type="text"
            class="form-control input-sm"
            :placeholder="'DLC '+ (index+1) +' name'"
            v-model="row.name"
          >
        </div>
        <div class="form-group input-group">
          <div class="input-group-prepend">
            <span class="input-group-text"><font-awesome-icon icon="key" fixed-width /></span>
          </div>
          <input
            type="text"
            class="form-control input-sm"
            :placeholder="'DLC '+ (index+1) +' key'"
            v-model="row.key"
          >
        </div>
        <div class="form-group input-group">
          <div class="input-group-prepend">
            <span class="input-group-text"><font-awesome-icon icon="edit" fixed-width /></span>
          </div>
          <input
            type="text"
            class="form-control input-sm"
            :placeholder="'DLC '+ (index+1) +' notes'"
            v-model="row.notes"
          >
        </div>
        <button type="button" v-on:click="removeRow(index);" class="btn btn-danger btn-sm">Remove</button>
      </div>
      <div v-if="loading" class="spinner-border text-secondary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <div v-if="!loading" class="modal-footer">
        <button type="button" class='btn btn-secondary'  @click='addRow()'>Add DLC</button>
        <button type="button" class="btn btn-secondary" @click="cancelEdit()">Cancel</button>
        <button type="submit" class="btn btn-success" @click="submitEdit(true)">Save & Update Match</button>
        <button type="button" class="btn btn-primary" @click="submitEdit(false)">Save Only</button>
      </div>
    </div>
  </div>
</template>

<script>
import gql from 'graphql-tag'

export default {
  name: 'Game',
  props: ['user'],
  data() {
    return {
      gameInfo: {},
      editErrors: [],
      updateError: '',
      loading: false
    }
  },
  mounted() {
    if (!this.userIsInGroup('redeemers')) {
      this.$router.push('/')
    }
  },
  methods: {
    userIsInGroup(group) {
      // The token payload holds an array of groups the user is in and indexOf will return the
      // index of the group name if it is found but -1 if it is not
      try {
        return this.user.signInUserSession.accessToken.payload['cognito:groups'].indexOf(group) > -1
      } catch {
        // User wasn't logged in so token didn't exist
        return false
      }
    },
    cancelEdit() {
      this.$router.push('/game/'+this.$route.params.gameId)
    },
    submitEdit(updateMatch) {
      // Validate input data
      this.editErrors = []
      this.submitError = ''

      // Check main key fields
      if (!this.gameInfo.name.length) {
        this.editErrors.push('Name is required')
      }
      if (!this.gameInfo.key.length) {
        this.editErrors.push('Key is required')
      }

      // Check DLC fields
      for (var i = 0; i < this.gameInfo.dlcs.length; i++) {
        if (!this.gameInfo.dlcs[i].name.length) {
          this.editErrors.push(`Name for DLC ${i+1} is required`)
        }
        if (!this.gameInfo.dlcs[i].key.length) {
          this.editErrors.push(`Key for DLC ${i+1} is required`)
        }
      }

      // If there weren't validation errors
      if (!this.editErrors.length) {
        this.loading = true
        // Build the mutation variables
        var input = {
          KeyId: this.gameInfo.KeyId,
          name: this.gameInfo.name,
          key: this.gameInfo.key,
          updater: this.user.username
        }

        // Grab notes if specified
        if (this.gameInfo.notes.length) {
          input.notes = this.gameInfo.notes
        }

        // Grab dlcs if there were any
        if (this.gameInfo.dlcs.length) {
          // Have to build the array dynamically because the dlcs list has a __typename field
          // which can't be included in the input variable
          input.dlcs = []
          for (var dlcIndex=0; dlcIndex<this.gameInfo.dlcs.length; dlcIndex++) {
            input.dlcs[dlcIndex] = {
              name: this.gameInfo.dlcs[dlcIndex].name,
              key: this.gameInfo.dlcs[dlcIndex].key
            }
            if (this.gameInfo.dlcs[dlcIndex].notes) {
              input.dlcs[dlcIndex].notes = this.gameInfo.dlcs[dlcIndex].notes
            }
          }
        }

        // Submit the mutation
        this.$apollo.mutate({
          mutation: gql`mutation ($KeyId: ID!, $name: String!, $key: String!, $notes: String, $updater: String!, $dlcs: [DlcInput!]) {
            updateKey(KeyId: $KeyId, name: $name, key: $key, notes: $notes, updater: $updater, dlcs: $dlcs) {
              KeyId
              name
              key
              notes
              rawgId
              submitter
              submitTime
              redeemer
              redeemed
              redeemTime
              updater
              updateTime
              dlcs {
                name
                key
                notes
              }
            }
          }`,
          variables: input
        }).then(() => {
          if (updateMatch) {
            // Go to the match page
            this.$router.push('/match/'+this.$route.params.gameId)
          } else {
            // Go back to game page
            this.$router.push('/game/'+this.$route.params.gameId)
          }
        }).catch((error) => {
          this.loading = false
          // Save error message
          this.updateError = error
          // Scroll back to the error (have to wait until the DOM gets re-rendered)
          this.$nextTick(function(){
            window.scrollTo(0,0)
          })
        })
      } else {
        // Scroll back to the error list (have to wait until the DOM gets re-rendered)
        this.$nextTick(function(){
          window.scrollTo(0,0)
        })
      }
    },
    addRow() {
      this.gameInfo.dlcs.push({name: '', key: '', notes: ''})
    },
    removeRow(index) {
      this.gameInfo.dlcs.splice(index, 1)
    }
  },
  apollo: {
    gameInfo: {
      query: gql`
        query getKey ($KeyId: ID!) {
          gameInfo: getKey(KeyId: $KeyId) {
            KeyId
            name
            key
            notes
            rawgId
            submitter
            submitTime
            redeemer
            redeemed
            redeemTime
            updater
            updateTime
            dlcs {
              name
              key
              notes
            }
          }
        }`
      ,
      variables () {
        return { KeyId: this.$route.params.gameId }
      },
      result () {
        // Notes can be null, default the var so it validations work
        if (!this.gameInfo.notes) {
          this.gameInfo.notes = ''
        }
        // Dlcs can be null, default so push (addRow) works
        if (!this.gameInfo.dlcs) {
          this.gameInfo.dlcs = []
        }
      }
    }
  }
}
</script>